'use strict';

/**
 * NACRE Wave 2.1.2 — symmetric primitive (AES-256-GCM)
 *
 * The "all encryption at rest" workhorse for the vault system. Wraps
 * Node's built-in `crypto` AES-256-GCM with a simple envelope format and
 * fresh-IV-per-encryption guarantees.
 *
 * Envelope shape:
 *   {
 *     algo: "v1.aes256gcm",
 *     iv:   "<24 hex chars (12 bytes)>",
 *     ct:   "<hex>",
 *     tag:  "<32 hex chars (16 bytes)>"
 *   }
 *
 * Why all-hex: JSON-friendly, deterministic length, no base64 padding
 * surprises. Storage cost is 2x raw bytes which is fine for the typical
 * envelope sizes (sub-KB metadata, few-KB messages).
 *
 * Why 96-bit IV: NIST SP 800-38D recommends 96-bit nonces for GCM (any
 * other length triggers an extra GHASH operation and weakens the security
 * proof). 96 bits + random generation gives us ~2^48 messages before
 * collision risk becomes nontrivial — far beyond any single user's
 * lifetime use.
 *
 * Why 256-bit key: matches the vault master key size (32 bytes) and
 * provides 256-bit security against classical attackers, ~128-bit
 * security against quantum (Grover's algorithm gives quadratic speedup).
 */

const crypto = require('crypto');

const ALGO_V1 = 'v1.aes256gcm';
const KEY_LENGTH = 32;
const IV_LENGTH = 12;
const TAG_LENGTH = 16;

const TEXT_ENCODER = new TextEncoder();
const TEXT_DECODER = new TextDecoder();

/**
 * Encrypt plaintext under a 32-byte key. Returns the v1 envelope.
 *
 * @param {string|Uint8Array} plaintext
 * @param {Uint8Array|Buffer} key — must be exactly 32 bytes
 * @returns {{algo: string, iv: string, ct: string, tag: string}}
 */
function encrypt(plaintext, key) {
  if (!(key instanceof Uint8Array) && !Buffer.isBuffer(key)) {
    throw new TypeError('Invalid key: must be Uint8Array or Buffer');
  }
  if (key.length !== KEY_LENGTH) {
    throw new Error('Invalid key: must be exactly 32 bytes (got ' + key.length + ')');
  }

  let plainBytes;
  if (typeof plaintext === 'string') {
    plainBytes = Buffer.from(TEXT_ENCODER.encode(plaintext));
  } else if (plaintext instanceof Uint8Array || Buffer.isBuffer(plaintext)) {
    plainBytes = Buffer.from(plaintext);
  } else {
    throw new TypeError('Invalid plaintext: must be string or Uint8Array');
  }

  const iv = crypto.randomBytes(IV_LENGTH);
  const cipher = crypto.createCipheriv('aes-256-gcm', Buffer.from(key), iv);
  const ct = Buffer.concat([cipher.update(plainBytes), cipher.final()]);
  const tag = cipher.getAuthTag();

  return {
    algo: ALGO_V1,
    iv: iv.toString('hex'),
    ct: ct.toString('hex'),
    tag: tag.toString('hex'),
  };
}

/**
 * Decrypt a v1 envelope under a 32-byte key.
 *
 * @param {{algo: string, iv: string, ct: string, tag: string}} envelope
 * @param {Uint8Array|Buffer} key — must be exactly 32 bytes
 * @param {object} [opts]
 * @param {boolean} [opts.binary=false] — if true, return Uint8Array; else string (UTF-8)
 * @returns {string|Uint8Array}
 * @throws if envelope is malformed, key is wrong, or auth tag verification fails
 */
function decrypt(envelope, key, opts) {
  const binary = !!(opts && opts.binary);

  if (!envelope || typeof envelope !== 'object') {
    throw new Error('Invalid envelope: must be an object');
  }
  // Use `=== undefined` not `!` because empty plaintext yields an empty
  // ct hex string which is falsy but valid.
  if (envelope.algo === undefined || envelope.iv === undefined ||
      envelope.ct === undefined || envelope.tag === undefined) {
    throw new Error('Invalid envelope: missing required fields (algo, iv, ct, tag)');
  }
  if (envelope.algo !== ALGO_V1) {
    throw new Error('Unsupported algo version: ' + envelope.algo + ' (expected ' + ALGO_V1 + ')');
  }
  if (!(key instanceof Uint8Array) && !Buffer.isBuffer(key)) {
    throw new TypeError('Invalid key: must be Uint8Array or Buffer');
  }
  if (key.length !== KEY_LENGTH) {
    throw new Error('Invalid key: must be exactly 32 bytes (got ' + key.length + ')');
  }

  const iv = Buffer.from(envelope.iv, 'hex');
  const ct = Buffer.from(envelope.ct, 'hex');
  const tag = Buffer.from(envelope.tag, 'hex');

  if (iv.length !== IV_LENGTH) {
    throw new Error('Invalid envelope: iv must be ' + IV_LENGTH + ' bytes');
  }
  if (tag.length !== TAG_LENGTH) {
    throw new Error('Invalid envelope: tag must be ' + TAG_LENGTH + ' bytes');
  }

  const decipher = crypto.createDecipheriv('aes-256-gcm', Buffer.from(key), iv);
  decipher.setAuthTag(tag);

  // GCM auth tag verification happens on `final()`. If the tag doesn't
  // match (wrong key, tampered ct, tampered iv, tampered tag), final()
  // throws "Unsupported state or unable to authenticate data".
  const decrypted = Buffer.concat([decipher.update(ct), decipher.final()]);

  if (binary) {
    return new Uint8Array(decrypted);
  }
  return TEXT_DECODER.decode(decrypted);
}

module.exports = {
  encrypt,
  decrypt,
  ALGO_V1,
  KEY_LENGTH,
  IV_LENGTH,
  TAG_LENGTH,
};
